import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useConnectModal } from 'thirdweb/react';
import { useTheme } from '../context/ThemeContext';
import ReactECharts from 'echarts-for-react';
import AppLayout from '../components/layout/AppLayout';
import Seo from '../components/Seo';
import { useWallet } from '../context/WalletContext';
import { useTranslation } from 'react-i18next';
import {
  getVaultData, getVaultChartDirect, createTransaction, createRedemption, completeRedemption,
  syncOnChainPositions, listUserRedemptions, cancelRedemption,
  getUserPortfolio,
} from '../lib/api';
import { getContract, prepareContractCall, waitForReceipt, estimateGas, readContract } from 'thirdweb';
import { getRpcClient, eth_gasPrice } from 'thirdweb/rpc';
import { useSendTransaction, useActiveAccount, useReadContract } from 'thirdweb/react';
import { thirdwebClient, targetChain, supportedWallets } from '../lib/thirdweb';


const USDC_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';

const TOKEN_LOGOS = {
  USDC:  '/usd-coin-usdc-logo.png',
  WETH:  '/weth-logotic.png',
  ETH:   '/ethereum-eth-logo.png',
  WBTC:  '/wrapped-bitcoin-wbtc-icon.svg',
  cbBTC: '/cbbtc.png',
  stETH: '/stETH_ug10fg.svg',
};

/* Hardcoded exposure by vault name (no API needed) */
const VAULT_EXPOSURE_MAP = {
  btc: [
    { symbol: 'WBTC',  logo: '/wrapped-bitcoin-wbtc-icon.svg' },
  ],
  eth: [
    { symbol: 'stETH', logo: '/stETH_ug10fg.svg' },
    { symbol: 'WETH',  logo: '/weth-logotic.png' },
    { symbol: 'WBTC',  logo: '/wrapped-bitcoin-wbtc-icon.svg' },
    { symbol: 'cbBTC', logo: '/cbbtc.png' },
  ],
};
function getExposureTokens(name = '') {
  const n = name.toLowerCase();
  if (n.includes('btc')) return VAULT_EXPOSURE_MAP.btc;
  return VAULT_EXPOSURE_MAP.eth;
}

const TOKEN_SYMBOL_MAP = {
  '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599': 'WBTC',
  '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2': 'WETH',
  '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': 'USDC',
  '0xdac17f958d2ee523a2206206994597c13d831ec7': 'USDT',
  '0x6b175474e89094c44da98b954eedeac495271d0f': 'DAI',
  '0xae7ab96520de3a18e5e111b5eaab095312d7fe84': 'stETH',
  '0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0': 'wstETH',
  '0x4d224452801aced8b2f0aebe155379bb5d594381': 'APE',
  '0x514910771af9ca656af840dff83e8264ecf986ca': 'LINK',
  '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984': 'UNI',
  '0xd533a949740bb3306d119cc777fa900ba034cd52': 'CRV',
  '0xba100000625a3754423978a60c9317c58a424e3d': 'BAL',
  '0x0bc529c00c6401aef6d220be8c6ea1667f6ad93e': 'YFI',
};

/* ── Pagination ── */
const ROWS_PER_PAGE = 10;
function Pagination({ total, page, onChange }) {
  const pages = Math.ceil(total / ROWS_PER_PAGE);
  if (pages <= 1) return null;
  const visible = new Set([0, pages - 1, page, page - 1, page + 1].filter(p => p >= 0 && p < pages));
  const sorted = [...visible].sort((a, b) => a - b);
  const items = [];
  let prev = -1;
  for (const p of sorted) {
    if (p - prev > 1) items.push('…');
    items.push(p);
    prev = p;
  }
  return (
    <div className="vp2-pagination">
      <button className="vp2-page-btn vp2-page-btn--nav" disabled={page === 0} onClick={() => onChange(page - 1)}>‹</button>
      {items.map((item, i) =>
        item === '…'
          ? <span key={`e${i}`} className="vp2-page-ellipsis">…</span>
          : <button key={item} className={`vp2-page-btn ${item === page ? 'active' : ''}`} onClick={() => onChange(item)}>{item + 1}</button>
      )}
      <button className="vp2-page-btn vp2-page-btn--nav" disabled={page === pages - 1} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

const PROTOCOL_LOGO_MAP = {
  aaveV3DebtPositionChange:         'https://icons.llamao.fi/icons/protocols/aave-v3',
  uniswapV3LiquidityPositionChange: 'https://icons.llamao.fi/icons/protocols/uniswap-v3',
  trade:                            'https://icons.llamao.fi/icons/protocols/bebop',
  deposit:                          'https://icons.llamao.fi/icons/protocols/morpho',
  withdraw:                         'https://icons.llamao.fi/icons/protocols/morpho',
  redeem:                           'https://icons.llamao.fi/icons/protocols/morpho',
  mint:                             'https://icons.llamao.fi/icons/protocols/morpho',
};

function tokenSymbol(address) {
  if (!address) return '?';
  return TOKEN_SYMBOL_MAP[address.toLowerCase()] ?? `${address.slice(0,6)}…`;
}
function fmtAmount(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return amount;
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (n >= 1) return n.toFixed(4).replace(/\.?0+$/, '');
  return n.toPrecision(4).replace(/\.?0+$/, '');
}
function fmtActUsd(value) {
  const n = parseFloat(value);
  if (isNaN(n)) return '';
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function TokenLogo({ address, size = 16 }) {
  const [err, setErr] = useState(false);
  if (!address || err) return null;
  return (
    <img
      src={`https://tokens.1inch.io/${address.toLowerCase()}.png`}
      onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'block' }}
      alt=""
    />
  );
}

function ProtocolLogo({ kind, size = 14 }) {
  const [err, setErr] = useState(false);
  const src = PROTOCOL_LOGO_MAP[kind];
  if (!src || err) return null;
  return (
    <img
      src={src}
      onError={() => setErr(true)}
      style={{ width: size, height: size, borderRadius: 3, flexShrink: 0, display: 'block' }}
      alt=""
    />
  );
}

function ActivityDetails({ kind, details }) {
  if (!details) return null;

  if (kind === 'trade') {
    const out = details.outgoingAssetAmounts?.[0];
    const inc = details.incomingAssetAmounts?.[0];
    if (!out && !inc) return null;
    const usd = inc?.currencyValue || out?.currencyValue;
    return (
      <span className="vp2-activity-detail">
        {out && (
          <span className="vp2-act-token">
            <TokenLogo address={out.asset} />
            <span className="vp2-act-out">{fmtAmount(out.amount)} {tokenSymbol(out.asset)}</span>
          </span>
        )}
        {out && inc && <span className="vp2-act-arrow">→</span>}
        {inc && (
          <span className="vp2-act-token">
            <TokenLogo address={inc.asset} />
            <span className="vp2-act-inc">{fmtAmount(inc.amount)} {tokenSymbol(inc.asset)}</span>
          </span>
        )}
        {usd && <span className="vp2-act-usd">{fmtActUsd(usd)}</span>}
      </span>
    );
  }

  if (kind === 'aaveV3DebtPositionChange') {
    const a = details.assetAmounts?.[0];
    if (!a) return null;
    return (
      <span className="vp2-activity-detail">
        <span className="vp2-act-token">
          <TokenLogo address={a.asset} />
          <span className="vp2-act-inc">{fmtAmount(a.amount)} {tokenSymbol(a.asset)}</span>
        </span>
        {a.currencyValue && <span className="vp2-act-usd">{fmtActUsd(a.currencyValue)}</span>}
      </span>
    );
  }

  if (kind === 'uniswapV3LiquidityPositionChange') {
    const t0 = details.nft?.token0;
    const t1 = details.nft?.token1;
    const fee = details.nft?.fee ? `${(parseFloat(details.nft.fee) * 100).toFixed(2)}%` : '';
    return (
      <span className="vp2-activity-detail">
        <span className="vp2-act-token-pair">
          <TokenLogo address={t0} />
          <TokenLogo address={t1} />
        </span>
        <span className="vp2-act-pair">{tokenSymbol(t0)}/{tokenSymbol(t1)}</span>
        {fee && <span className="vp2-act-fee-tier">{fee}</span>}
      </span>
    );
  }

  return null;
}

function riskDots(level) {
  if (typeof level === 'number') {
    const n = Math.min(5, Math.max(1, Math.round(level)));
    return '●'.repeat(n) + '○'.repeat(5 - n);
  }
  const map = { low: 2, medium: 3, high: 4 };
  const n = map[String(level ?? '').toLowerCase()] ?? 3;
  return '●'.repeat(n) + '○'.repeat(5 - n);
}

/* ── Formatters ── */
function fmtUsd(v) {
  if (v == null) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}
function fmtPct(v) { return v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtFee(v) { return v != null ? `${parseFloat(v).toFixed(2)}%` : '0.00%'; }
function fmtAum(v) {
  if (!v) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
}

/* ── ECharts vault chart ── */
function VaultChart({ data, positive, mode = 'price', timeframe = '1M', theme = 'dark', markers = [] }) {
  const color = mode === 'aum' ? '#6366f1' : (positive ? '#22C55E' : '#EF4444');
  const isLight = theme === 'light';

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  const option = useMemo(() => {
    const vals = data.map(d => d.v);
    const dataMin = vals.length ? Math.min(...vals) : 0;
    const dataMax = vals.length ? Math.max(...vals) : 0;
    const dataRange = dataMax - dataMin;
    // Pick decimal precision based on range — tighter range → more decimals
    const aumDecimals = dataRange < 500 ? 2 : dataRange < 5000 ? 1 : 0;

    return ({
    animation: { duration: 500 },
    grid: { top: isMobile ? 88 : 12, bottom: 36, left: 4, right: isMobile ? 46 : 64 },
    xAxis: {
      type: 'time',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      splitNumber: isMobile ? 4 : 6,
      axisLabel: {
        color: isLight ? '#888' : 'rgba(255,255,255,0.55)',
        fontSize: isMobile ? 9 : 11,
        fontFamily: 'JetBrains Mono',
        formatter: (val) => {
          const d = new Date(val);
          if (timeframe === '1D') {
            return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
          }
          return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth() + 1).padStart(2,'0')}`;
        },
        margin: 10,
      },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      scale: true,
      splitLine: { lineStyle: { color: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.06)' } },
      axisLabel: {
        color: isLight ? '#888' : 'rgba(255,255,255,0.55)', fontSize: isMobile ? 9 : 11, fontFamily: 'JetBrains Mono',
        formatter: (v) => {
          if (mode !== 'aum') return `$${v.toFixed(3)}`;
          if (v >= 1e6) return `$${(v/1e6).toFixed(aumDecimals)}M`;
          if (v >= 1e3) return `$${(v/1e3).toFixed(aumDecimals)}K`;
          return `$${v.toFixed(2)}`;
        },
        margin: 8,
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'line',
        data: data.map(d => [d.t, d.v]),
        smooth: 0.3,
        symbol: 'circle',
        symbolSize: 5,
        showSymbol: false,
        lineStyle: { color, width: 2 },
        // Subtle vertical lines at each group — always declared, data is empty when no markers
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          animation: false,
          data: markers.map(m => {
            const netGreen = !m.hasWithdrawal || m.depositTotal >= m.withdrawalTotal;
            const lineColor = netGreen ? 'rgba(34,197,94,0.22)' : 'rgba(239,68,68,0.22)';
            return {
              xAxis: m.t,
              lineStyle: { color: lineColor, type: 'dashed', width: 1 },
              label: { show: false },
            };
          }),
        },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0,   color: mode === 'aum' ? 'rgba(99,102,241,0.18)' : (positive ? 'rgba(34,197,94,0.18)' : 'rgba(239,68,68,0.18)') },
              { offset: 0.7, color: mode === 'aum' ? 'rgba(99,102,241,0.03)' : (positive ? 'rgba(34,197,94,0.03)' : 'rgba(239,68,68,0.03)') },
              { offset: 1,   color: 'rgba(0,0,0,0)' },
            ],
          },
        },
      },
      // Grouped markers — always declared; data is [] when D/W disabled or no markers.
      // Keeping the series alive avoids ECharts setOption merge keeping stale circles.
      {
        type: 'scatter',
        symbolSize: 10,
        symbol: 'circle',
        zlevel: 5,
        cursor: 'pointer',
        label: { show: false },
        data: markers.map(m => {
          const netGreen = !m.hasWithdrawal || m.depositTotal >= m.withdrawalTotal;
          return {
            value: [m.t, m.y],
            hasDeposit: m.hasDeposit,
            hasWithdrawal: m.hasWithdrawal,
            depositTotal: m.depositTotal,
            withdrawalTotal: m.withdrawalTotal,
            items: m.items,
            itemStyle: {
              color: netGreen ? 'rgba(34,197,94,0.85)' : 'rgba(239,68,68,0.85)',
              borderColor: isLight ? '#fff' : 'rgba(255,255,255,0.8)',
              borderWidth: 1.5,
            },
          };
        }),
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: { lineStyle: { color: 'rgba(255,255,255,0.15)' } },
      backgroundColor: 'rgba(23,23,23,0.96)',
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth: 1,
      textStyle: { fontFamily: 'JetBrains Mono', fontSize: 12, color: '#fff' },
      formatter: (params) => {
        if (!Array.isArray(params)) return '';
        const lineP = params.find(p => p.seriesIndex === 0);
        const scatterP = params.find(p => p.seriesIndex === 1);
        if (!lineP) return '';

        const d = new Date(lineP.value[0]);
        const dateStr = d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
        const val = lineP.value[1];
        const valStr = mode === 'aum'
          ? val >= 1e6
            ? `$${(val/1e6).toFixed(2)}M`
            : val >= 1e3
              ? `$${(val/1e3).toFixed(2)}K`
              : val.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : `$${val.toFixed(4)}`;

        // Find nearest marker by scanning the markers array directly
        // (scatterP in params is unreliable with trigger:'axis' since timestamps don't align)
        const snapThr = { '1D': 360_000, '1W': 2_880_000, '1M': 8_640_000, 'ALL': 34_560_000 };
        const thr = snapThr[timeframe] ?? 21_600_000;
        const cursorT = lineP.value[0];
        const nearMarker = markers.reduce(
          (best, m) => Math.abs(m.t - cursorT) < Math.abs((best?.t ?? Infinity) - cursorT) ? m : best,
          null
        );
        const showMarkerDetail = nearMarker && Math.abs(nearMarker.t - cursorT) < thr;

        let html = `<div style="font-size:10px;color:#959593;margin-bottom:3px">${dateStr}</div>`;
        html += `<div style="font-weight:700${showMarkerDetail ? ';margin-bottom:6px' : ''}">${valStr}</div>`;

        if (showMarkerDetail) {
          const { hasDeposit, hasWithdrawal, depositTotal, withdrawalTotal, items } = nearMarker;
          const fmtAmt = v => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
          html += `<div style="border-top:1px solid rgba(255,255,255,0.08);padding-top:5px">`;
          if (hasDeposit) {
            html += `<div style="color:#22C55E;font-weight:700">▲ Deposit${items.filter(i=>i.type==='deposit').length>1?'s':''} · ${fmtAmt(depositTotal)}</div>`;
          }
          if (hasWithdrawal) {
            html += `<div style="color:#EF4444;font-weight:700">▼ Withdrawal${items.filter(i=>i.type==='withdrawal').length>1?'s':''} · ${fmtAmt(withdrawalTotal)}</div>`;
          }
          if (items.length > 1) {
            items.forEach(item => {
              const sign = item.type === 'deposit' ? '+' : '−';
              const c = item.type === 'deposit' ? '#22C55E' : '#EF4444';
              html += `<div style="color:${c};font-size:10px;margin-left:8px;opacity:0.8">${sign} ${fmtAmt(item.usdc)}</div>`;
            });
          }
          html += `</div>`;
        }
        return html;
      },
    },
    });
  }, [data, positive, color, mode, timeframe, markers, isLight, isMobile]);

  const { t } = useTranslation('vault');
  if (!data || data.length < 2) {
    return <div className="vp2-chart-empty">{t('noChartData')}</div>;
  }

  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} />;
}

/* ── Pending banner ── */
function PendingBanner({ redemption, vaultSymbol, sharePrice, onCancel, onExecute, canceling }) {
  const { t } = useTranslation('vault');
  const [secsLeft, setSecsLeft] = useState(() =>
    Math.max(0, Math.floor((new Date(redemption.unlock_at).getTime() - Date.now()) / 1000))
  );
  useEffect(() => {
    if (secsLeft <= 0) return;
    const id = setInterval(() => setSecsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [redemption.unlock_at]);

  const unlocked = secsLeft <= 0;
  const h = Math.floor(secsLeft / 3600);
  const m = Math.floor((secsLeft % 3600) / 60);
  const s = secsLeft % 60;
  const timeStr = h > 0 ? `${h}h ${String(m).padStart(2,'0')}m` : m > 0 ? `${m}m ${String(s).padStart(2,'0')}s` : `${s}s`;
  const shares = Number(redemption.shares_amount ?? 0);
  const estUsdc = Number(redemption.estimated_usdc_value ?? shares * sharePrice);

  return (
    <div className={`vp2-pending ${unlocked ? 'vp2-pending-ready' : ''}`}>
      <div className="vp2-pending-top">
        <i className={`ph-bold ${unlocked ? 'ph-check-circle' : 'ph-clock-countdown'}`} />
        <span>{unlocked ? t('readyToExecute') : t('pendingWithdrawal')}</span>
        {!unlocked && <span className="vp2-pending-timer">{timeStr}</span>}
      </div>
      <div className="vp2-pending-detail">
        {shares.toFixed(4)} {vaultSymbol} ≈ {fmtUsd(estUsdc)}
      </div>
      <div className="vp2-pending-btns">
        {unlocked && (
          <button className="vp2-pending-exec" onClick={onExecute}>{t('execute')}</button>
        )}
        <button className="vp2-pending-cancel" disabled={canceling} onClick={onCancel}>
          {canceling ? <i className="ph-bold ph-spinner spin-icon" /> : null} {t('cancel')}
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════ */
export default function VaultPage() {
  const { t } = useTranslation('vault');
  const { vaultId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { walletAddress, isThirdweb, isConnected } = useWallet();
  const { theme } = useTheme();
  const account = useActiveAccount();
  const { mutateAsync: sendTransaction } = useSendTransaction();
  const { connect } = useConnectModal();

  const urlRedemptionId = searchParams.get('redemption_id');
  const urlShares = searchParams.get('shares');
  const isFinalize = !!urlRedemptionId;

  /* ── State ── */
  const [vaultData, setVaultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('1M');
  const [mode, setMode] = useState(isFinalize ? 'withdraw' : 'deposit');
  const [amount, setAmount] = useState('');
  const [useMaxShares, setUseMaxShares] = useState(false);
  const [txStatus, setTxStatus] = useState('idle');
  const [txError, setTxError] = useState('');
  const [gasUSD, setGasUSD] = useState(null);
  const [gasTimer, setGasTimer] = useState(30);
  const [pendingRedemptions, setPendingRedemptions] = useState([]);
  const [cancelingId, setCancelingId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('depositors');
  const [tabPage, setTabPage] = useState(0);
  const [chartMode, setChartMode] = useState('price'); // 'price' | 'aum'
  const [showMarkers, setShowMarkers] = useState(true);
  const [userTxs, setUserTxs] = useState([]);
  const [vaultDeposits, setVaultDeposits] = useState([]);
  const [vaultWithdrawals, setVaultWithdrawals] = useState([]);
  const [vaultAllTxs, setVaultAllTxs] = useState([]);
  const [vaultOnchainActivity, setVaultOnchainActivity] = useState([]);
  const [depositors, setDepositors] = useState([]);
  const [loadTime] = useState(() => new Date());
  const gasIntervalRef = useRef(null);

  /* ── USDC contract ── */
  const usdcContract = useMemo(() => getContract({
    client: thirdwebClient, chain: targetChain, address: USDC_ADDRESS,
  }), []);

  const { data: rawUsdcBalance } = useReadContract({
    contract: usdcContract,
    method: 'function balanceOf(address) view returns (uint256)',
    params: [walletAddress ?? '0x0000000000000000000000000000000000000000'],
    queryOptions: { enabled: !!walletAddress && isThirdweb },
  });
  const walletUsdcBalance = rawUsdcBalance ? Number(rawUsdcBalance) / 1e6 : 0;

  /* ── Direct chart data from vault_snapshots ── */
  const [directChart, setDirectChart] = useState([]);
  const [lastSnapshotTime, setLastSnapshotTime] = useState(null);

  /* ── Load vault ── */
  const loadVault = useCallback(async () => {
    setLoading(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const [data, chart, extra, lastSnap] = await Promise.allSettled([
        getVaultData(vaultId, walletAddress, timeframe),
        getVaultChartDirect(vaultId, timeframe),
        supabase.from('vaults').select('manager_address, fee_recipient_address').eq('id', vaultId).single(),
        supabase.from('vault_snapshots').select('timestamp').eq('vault_id', vaultId).order('timestamp', { ascending: false }).limit(1).single(),
      ]);
      if (data.status === 'fulfilled') {
        const vd = data.value;
        if (vd?.vault && extra.status === 'fulfilled' && extra.value?.data) {
          Object.assign(vd.vault, extra.value.data);
        }
        setVaultData(vd);
      } else console.error('VaultPage load error:', data.reason);
      if (chart.status === 'fulfilled') setDirectChart(chart.value);
      else console.error('VaultPage chart error:', chart.reason);
      if (lastSnap.status === 'fulfilled' && lastSnap.value?.data?.timestamp) {
        setLastSnapshotTime(new Date(lastSnap.value.data.timestamp));
      }
    } finally {
      setLoading(false);
    }
  }, [vaultId, walletAddress, timeframe]);

  useEffect(() => { loadVault(); }, [loadVault]);

  /* ── Load user activity for this vault ── */
  useEffect(() => {
    if (!walletAddress) { setUserTxs([]); return; }
    getUserPortfolio(walletAddress, 'ALL')
      .then(p => {
        const all = p?.transactions ?? [];
        setUserTxs(all.filter(tx => tx.vault_id === vaultId));
      })
      .catch(() => setUserTxs([]));
  }, [walletAddress, vaultId]);

  /* ── Load vault-wide activity ── */
  const loadVaultActivity = useCallback(() => {
    import('../lib/supabase').then(({ supabase }) => {
      const sel = 'wallet_address, tx_type, usdc_amount, shares_amount, share_price_at_tx, timestamp, tx_hash';
      Promise.all([
        supabase.from('user_transactions').select(sel)
          .eq('vault_id', vaultId).eq('tx_type', 'deposit')
          .order('timestamp', { ascending: false }).limit(200),
        supabase.from('user_transactions').select(sel)
          .eq('vault_id', vaultId).eq('tx_type', 'withdrawal')
          .order('timestamp', { ascending: false }).limit(200),
        supabase.from('user_positions').select('wallet_address, current_shares, total_deposited_usdc, total_withdrawn_usdc')
          .eq('vault_id', vaultId).gt('current_shares', 0)
          .order('current_shares', { ascending: false }),
        supabase.from('vault_activity')
          .select('tx_hash, block_number, timestamp, method_name, activity_kind, activity_details')
          .eq('vault_id', vaultId)
          .in('activity_kind', ['trade', 'aaveV3DebtPositionChange', 'uniswapV3LiquidityPositionChange', 'deposit', 'withdraw', 'redeem', 'mint'])
          .order('block_number', { ascending: false }).limit(200),
      ]).then(([deps, wdws, pos, activity]) => {
        const d = deps.data ?? [];
        const w = wdws.data ?? [];
        setVaultDeposits(d);
        setVaultWithdrawals(w);
        setVaultAllTxs([...d, ...w].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
        setDepositors(pos.data ?? []);
        setVaultOnchainActivity((activity.data ?? []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      }).catch(() => {});
    });
  }, [vaultId]);

  useEffect(() => { loadVaultActivity(); }, [loadVaultActivity]);

  // Reset pagination on tab change
  useEffect(() => { setTabPage(0); }, [activeTab]);

  /* ── Realtime: refresh activity on new tx ── */
  useEffect(() => {
    let sub;
    import('../lib/supabase').then(({ supabase }) => {
      sub = supabase.channel(`vault-activity-${vaultId}`)
        .on('postgres_changes', {
          event: 'INSERT', schema: 'public', table: 'user_transactions',
          filter: `vault_id=eq.${vaultId}`,
        }, () => { loadVaultActivity(); loadVault(); })
        .subscribe();
    });
    return () => { if (sub) import('../lib/supabase').then(({ supabase }) => supabase.removeChannel(sub)); };
  }, [vaultId, loadVaultActivity, loadVault]);

  /* ── Derived vault info ── */
  const vault = vaultData?.vault;
  const config = vaultData?.config;
  const perf = vaultData?.perf;
  const userPos = vaultData?.user_position;
  const isMorpho = vault?.provider === 'morpho';
  const managerAddress    = vault?.manager_address?.toLowerCase() ?? null;
  const feeRecipientAddress = vault?.fee_recipient_address?.toLowerCase() ?? null;
  const comptrollerAddress = config?.comptroller_address ?? null;
  const sharePrice = perf?.share_price ?? 1;
  const depositorCount = depositors.filter(d => d.wallet_address?.toLowerCase() !== feeRecipientAddress).length || perf?.depositors || '—';
  const vaultSymbol = vault?.symbol ?? 'bG12';

  const chartData = useMemo(() => {
    const pts = directChart.map(p => ({ t: p.t, v: p.share_price }));
    if (perf?.share_price) pts.push({ t: Date.now(), v: perf.share_price });
    return pts;
  }, [directChart, perf?.share_price]);

  const aumData = useMemo(() => {
    const pts = directChart
      .filter(p => p.total_aum != null)
      .map(p => ({ t: p.t, v: p.total_aum }));
    if (perf?.total_aum) pts.push({ t: Date.now(), v: perf.total_aum });
    return pts;
  }, [directChart, perf?.total_aum]);

  const activeChartData = chartMode === 'aum' ? aumData : chartData;

  /* ── Chart markers from vault-wide txs (grouped, positioned on AUM curve) ── */
  const chartMarkers = useMemo(() => {
    if (chartMode !== 'aum' || !showMarkers || !aumData.length) return [];
    const chartStart = aumData[0].t;
    const raw = vaultAllTxs
      .filter(tx => tx.timestamp && new Date(tx.timestamp).getTime() >= chartStart)
      .map(tx => ({ t: new Date(tx.timestamp).getTime(), type: tx.tx_type, usdc: Number(tx.usdc_amount) }))
      .sort((a, b) => a.t - b.t);
    if (!raw.length) return [];

    // Group proximity threshold by timeframe
    const thresholds = { '1D': 3_600_000, '1W': 14_400_000, '1M': 43_200_000, 'ALL': 172_800_000 };
    const threshold = thresholds[timeframe] ?? 43_200_000;

    // Cluster nearby markers
    const groups = [];
    let curr = [raw[0]];
    for (let i = 1; i < raw.length; i++) {
      if (raw[i].t - curr[curr.length - 1].t <= threshold) {
        curr.push(raw[i]);
      } else {
        groups.push(curr);
        curr = [raw[i]];
      }
    }
    groups.push(curr);

    // Interpolate AUM value at each group's midpoint timestamp
    function interpY(t) {
      if (t <= aumData[0].t) return aumData[0].v;
      if (t >= aumData[aumData.length - 1].t) return aumData[aumData.length - 1].v;
      for (let i = 0; i < aumData.length - 1; i++) {
        const a = aumData[i], b = aumData[i + 1];
        if (t >= a.t && t <= b.t) {
          return a.v + (b.v - a.v) * (t - a.t) / (b.t - a.t);
        }
      }
      return aumData[aumData.length - 1].v;
    }

    // Snap timestamp to nearest aumData point
    function snapToNearest(t) {
      let best = aumData[0];
      let minDiff = Math.abs(aumData[0].t - t);
      for (const d of aumData) {
        const diff = Math.abs(d.t - t);
        if (diff < minDiff) { minDiff = diff; best = d; }
      }
      return best;
    }

    return groups.map(items => {
      const mid = Math.round(items[0].t + (items[items.length - 1].t - items[0].t) / 2);
      const snapped = snapToNearest(mid);
      const t = snapped.t;
      const y = snapped.v;
      const hasDeposit    = items.some(m => m.type === 'deposit');
      const hasWithdrawal = items.some(m => m.type === 'withdrawal');
      const depositTotal  = items.filter(m => m.type === 'deposit').reduce((s, m) => s + m.usdc, 0);
      const withdrawalTotal = items.filter(m => m.type === 'withdrawal').reduce((s, m) => s + m.usdc, 0);
      return { t, y, items, hasDeposit, hasWithdrawal, depositTotal, withdrawalTotal };
    });
  }, [vaultAllTxs, aumData, timeframe, chartMode, showMarkers]);

  const sharpeRatio = useMemo(() => {
    if (chartData.length < 10) return null;
    const returns = [];
    for (let i = 1; i < chartData.length; i++) {
      const prev = chartData[i - 1].v;
      if (prev > 0) returns.push((chartData[i].v - prev) / prev);
    }
    if (returns.length < 5) return null;
    const n = returns.length;
    const mean = returns.reduce((s, r) => s + r, 0) / n;
    const std = Math.sqrt(returns.reduce((s, r) => s + (r - mean) ** 2, 0) / n);
    if (std === 0) return null;
    return ((mean / std) * Math.sqrt(365)).toFixed(2);
  }, [chartData]);

  const inceptionStr = vault?.inception_date
    ? new Date(vault.inception_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;

  const lastUpdateStr = (lastSnapshotTime ?? loadTime).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });

  const chartPositive = chartData.length >= 2
    ? chartData[chartData.length - 1].v >= chartData[0].v
    : true;

  /* ── Pending redemptions ── */
  useEffect(() => {
    if (!walletAddress || !vault || mode !== 'withdraw' || isFinalize || isMorpho) {
      setPendingRedemptions([]); return;
    }
    listUserRedemptions(walletAddress)
      .then(res => {
        const all = res?.requests ?? [];
        setPendingRedemptions(all.filter(r => r.vault_id === vaultId && r.status === 'pending'));
      })
      .catch(() => setPendingRedemptions([]));
  }, [walletAddress, vaultId, mode, vault]);

  /* ── Gas estimation ── */
  useEffect(() => {
    clearInterval(gasIntervalRef.current);
    if (!vault) { setGasUSD(null); return; }
    fetchGasEstimate();
    gasIntervalRef.current = setInterval(() => {
      setGasTimer(prev => { if (prev <= 1) { fetchGasEstimate(); return 30; } return prev - 1; });
    }, 1000);
    return () => clearInterval(gasIntervalRef.current);
  }, [vaultId, mode, vault?.address]);

  async function fetchGasEstimate() {
    if (!vault?.address) return;
    try {
      const rpcRequest = getRpcClient({ client: thirdwebClient, chain: targetChain });
      let txGasPromise;
      if (isMorpho) {
        txGasPromise = Promise.resolve(mode === 'deposit' ? 170000 : 120000);
      } else if (mode === 'deposit' && comptrollerAddress) {
        const sampleAmount = BigInt(Math.round(1000 * 1e6));
        const cc = getContract({ client: thirdwebClient, chain: targetChain, address: comptrollerAddress });
        txGasPromise = Promise.all([
          estimateGas(prepareContractCall({ contract: usdcContract, method: 'function approve(address spender, uint256 amount) returns (bool)', params: [comptrollerAddress, sampleAmount] })),
          estimateGas(prepareContractCall({ contract: cc, method: 'function buyShares(uint256 _investmentAmount, uint256 _minSharesQuantity)', params: [sampleAmount, 1n] })),
        ]).then(([a, b]) => Number(a) + Number(b)).catch(() => 246000);
      } else if (comptrollerAddress) {
        const cc = getContract({ client: thirdwebClient, chain: targetChain, address: comptrollerAddress });
        txGasPromise = estimateGas(prepareContractCall({
          contract: cc,
          method: 'function redeemSharesForSpecificAssets(address _recipient, uint256 _sharesQuantity, address[] _payoutAssets, uint256[] _payoutAssetPercentages)',
          params: [walletAddress ?? '0x0000000000000000000000000000000000000000', BigInt(1e15), [USDC_ADDRESS], [10000n]],
        })).then(Number).catch(() => 250000);
      } else {
        txGasPromise = Promise.resolve(200000);
      }
      const [gasPriceWei, ethRes, totalGas] = await Promise.all([
        eth_gasPrice(rpcRequest).catch(() => BigInt(20e9)),
        fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd').catch(() => null),
        txGasPromise,
      ]);
      const gasPriceGwei = Number(gasPriceWei) / 1e9;
      let ethPriceUSD = 3000;
      if (ethRes?.ok) { const j = await ethRes.json(); ethPriceUSD = j.ethereum?.usd ?? 3000; }
      setGasUSD((gasPriceGwei * totalGas) / 1e9 * ethPriceUSD);
      setGasTimer(30);
    } catch { /* keep previous */ }
  }

  /* ── Transaction math ── */
  const amountNum = isFinalize
    ? (Number(urlShares) * sharePrice)
    : (parseFloat(amount) || 0);
  const maxBalance = mode === 'deposit' ? walletUsdcBalance : (userPos?.current_shares ?? 0);
  const feeRate = mode === 'deposit'
    ? (isMorpho ? 0 : parseFloat(config?.entrance_fee_pct ?? 0) / 100)
    : (isMorpho ? 0 : parseFloat(config?.exit_fee_pct ?? 0) / 100);
  const usdcEquivalent = mode === 'withdraw' && !isFinalize ? amountNum * sharePrice : amountNum;
  const feeAmount = usdcEquivalent * feeRate;
  const netAmount = usdcEquivalent - feeAmount;
  const shares = isFinalize
    ? Number(urlShares ?? 0)
    : mode === 'withdraw' ? amountNum : (netAmount / (sharePrice || 1));
  const isOverBalance = !isFinalize && mode === 'withdraw' && amountNum > maxBalance;

  /* ── Transactions (logic identical to ManageCapital) ── */
  async function executeDeposit() {
    if (!isThirdweb || !account) { setTxError('Connect a real wallet to transact.'); return; }
    if (isMorpho) return executeMorphoDeposit();
    if (!comptrollerAddress) { setTxError('Comptroller address not found.'); return; }
    const amountRaw = BigInt(Math.round(amountNum * 1e6));
    setTxError('');
    try {
      setTxStatus('approving');
      const approveTx = prepareContractCall({ contract: usdcContract, method: 'function approve(address spender, uint256 amount) returns (bool)', params: [comptrollerAddress, amountRaw] });
      const approveResult = await sendTransaction(approveTx);
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: approveResult.transactionHash });
      setTxStatus('loading');
      const cc = getContract({ client: thirdwebClient, chain: targetChain, address: comptrollerAddress });
      const expectedSharesScaled = Math.floor((amountNum / (sharePrice || 1)) * 0.99 * 1e6);
      const minSharesRaw = BigInt(expectedSharesScaled) * 1_000_000_000_000n;
      const safeMinShares = minSharesRaw > 0n ? minSharesRaw : 1n;
      const buyTx = prepareContractCall({ contract: cc, method: 'function buyShares(uint256 _investmentAmount, uint256 _minSharesQuantity)', params: [amountRaw, safeMinShares] });
      const buyResult = await sendTransaction(buyTx);
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: buyResult.transactionHash });
      createTransaction({ wallet_address: walletAddress, vault_id: vaultId, tx_type: 'deposit', usdc_amount: amountNum, shares_amount: shares, share_price_at_tx: sharePrice, tx_hash: buyResult.transactionHash, status: 'confirmed', source: 'app' }).catch(() => {});
      setTxStatus('success');
      syncOnChainPositions(walletAddress).catch(() => {});
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
      else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
    }
  }

  async function executeMorphoDeposit() {
    const amountRaw = BigInt(Math.round(amountNum * 1e6));
    setTxError('');
    try {
      setTxStatus('approving');
      const approveTx = prepareContractCall({ contract: usdcContract, method: 'function approve(address spender, uint256 amount) returns (bool)', params: [vault.address, amountRaw] });
      const approveResult = await sendTransaction(approveTx);
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: approveResult.transactionHash });
      setTxStatus('loading');
      const morphoContract = getContract({ client: thirdwebClient, chain: targetChain, address: vault.address });
      const depositTx = prepareContractCall({ contract: morphoContract, method: 'function deposit(uint256 assets, address onBehalf) returns (uint256 shares)', params: [amountRaw, walletAddress] });
      const depositResult = await sendTransaction(depositTx);
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: depositResult.transactionHash });
      createTransaction({ wallet_address: walletAddress, vault_id: vaultId, tx_type: 'deposit', usdc_amount: amountNum, shares_amount: shares, share_price_at_tx: sharePrice, tx_hash: depositResult.transactionHash, status: 'confirmed', source: 'app' }).catch(() => {});
      setTxStatus('success');
      syncOnChainPositions(walletAddress).catch(() => {});
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
      else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
    }
  }

  async function executeMorphoWithdraw() {
    if (!isThirdweb || !account) { setTxError('Connect a real wallet to transact.'); return; }
    setTxError('');
    try {
      setTxStatus('loading');
      const morphoContract = getContract({ client: thirdwebClient, chain: targetChain, address: vault.address });
      const sharesForRedemption = useMaxShares ? (userPos?.current_shares ?? 0) : amountNum;
      const sharesStr = String(sharesForRedemption ?? '0');
      const [intPart, fracPart = ''] = sharesStr.split('.');
      const frac18 = (fracPart + '0'.repeat(18)).slice(0, 18);
      const sharesRaw = BigInt(intPart) * 10n ** 18n + BigInt(frac18);
      const redeemTx = prepareContractCall({ contract: morphoContract, method: 'function redeem(uint256 shares, address receiver, address onBehalf) returns (uint256 assets)', params: [sharesRaw, walletAddress, walletAddress] });
      let result;
      try { result = await sendTransaction(redeemTx); }
      catch (err) {
        const msg = err?.message ?? '';
        if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
        else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
        return;
      }
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: result.transactionHash });
      const approxUsdc = sharesForRedemption * sharePrice;
      createTransaction({ wallet_address: walletAddress, vault_id: vaultId, tx_type: 'withdrawal', usdc_amount: approxUsdc, shares_amount: sharesForRedemption, share_price_at_tx: sharePrice, tx_hash: result.transactionHash, status: 'confirmed', source: 'app' }).catch(() => {});
      setTxStatus('success');
      syncOnChainPositions(walletAddress).catch(() => {});
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
      else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
    }
  }

  async function executeWithdrawal() {
    if (isMorpho) return executeMorphoWithdraw();
    if (!walletAddress) return;
    setTxError('');
    try {
      setTxStatus('loading');
      const sharesForRedemption = useMaxShares ? (userPos?.current_shares ?? 0) : amountNum;
      await createRedemption(walletAddress, vaultId, sharesForRedemption);
      setTxStatus('pending_timelock');
    } catch (err) {
      setTxError(err?.message || 'Request failed.');
      setTxStatus('error');
    }
  }

  async function executeFinalize() {
    if (!isThirdweb || !account) { setTxError('Connect a real wallet to transact.'); return; }
    if (!vault?.address || !comptrollerAddress) { setTxError('Vault or comptroller address not found.'); return; }
    setTxError('');
    try {
      setTxStatus('loading');
      const vaultContract = getContract({ client: thirdwebClient, chain: targetChain, address: vault.address });
      const cc = getContract({ client: thirdwebClient, chain: targetChain, address: comptrollerAddress });
      const onChainBalance = await readContract({ contract: vaultContract, method: 'function balanceOf(address) view returns (uint256)', params: [walletAddress] });
      const sharesStr = String(urlShares ?? '0');
      const [intPart, fracPart = ''] = sharesStr.split('.');
      const frac18 = (fracPart + '0'.repeat(18)).slice(0, 18);
      const requestedRaw = BigInt(intPart) * 10n ** 18n + BigInt(frac18);
      const sharesRaw = requestedRaw >= onChainBalance ? onChainBalance : requestedRaw;
      const redeemTx = prepareContractCall({
        contract: cc,
        method: 'function redeemSharesForSpecificAssets(address _recipient, uint256 _sharesQuantity, address[] _payoutAssets, uint256[] _payoutAssetPercentages)',
        params: [walletAddress, sharesRaw, [USDC_ADDRESS], [10000n]],
      });
      let result;
      try { result = await sendTransaction(redeemTx); }
      catch (err) {
        const msg = err?.message ?? '';
        if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
        else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
        return;
      }
      await waitForReceipt({ client: thirdwebClient, chain: targetChain, transactionHash: result.transactionHash });
      await completeRedemption(walletAddress, urlRedemptionId, result.transactionHash);
      const exitFeeRate = isMorpho ? 0 : parseFloat(config?.exit_fee_pct ?? 0) / 100;
      const netShares = Number(urlShares) * (1 - exitFeeRate);
      const approxUsdc = netShares * sharePrice;
      createTransaction({ wallet_address: walletAddress, vault_id: vaultId, tx_type: 'withdrawal', usdc_amount: approxUsdc, shares_amount: netShares, share_price_at_tx: sharePrice, tx_hash: result.transactionHash, status: 'confirmed', source: 'app' }).catch(() => {});
      setTxStatus('success');
      syncOnChainPositions(walletAddress).catch(() => {});
    } catch (err) {
      const msg = err?.message ?? '';
      if (msg.includes('User rejected') || msg.includes('rejected') || msg.includes('denied')) setTxStatus('idle');
      else { setTxError(msg || 'Transaction failed.'); setTxStatus('error'); }
    }
  }

  function executeTransaction() {
    if (isFinalize) executeFinalize();
    else if (mode === 'deposit') executeDeposit();
    else executeWithdrawal();
  }

  async function handleConnect() {
    try {
      await connect({ client: thirdwebClient, chain: targetChain, wallets: supportedWallets, showThirdwebBranding: false, title: 'Connect to G12 Labs' });
    } catch (err) { if (err?.message !== 'Connection closed') console.error(err); }
  }

  async function handleCancelPending(redemptionId) {
    setCancelingId(redemptionId);
    try {
      await cancelRedemption(walletAddress, redemptionId);
      setPendingRedemptions(prev => prev.filter(r => r.id !== redemptionId));
    } catch (err) { console.error('Cancel failed:', err); }
    finally { setCancelingId(null); }
  }

  function handleFinalizePending(redemption) {
    navigate(`/vault/${vaultId}?redemption_id=${redemption.id}&shares=${redemption.shares_amount}`);
  }

  function resetForm() { setTxStatus('idle'); setTxError(''); setAmount(''); }

  /* ── Button state ── */
  const canSubmit = amountNum > 0 && !isOverBalance && txStatus === 'idle';
  const canFinalize = isFinalize && txStatus === 'idle';

  let btnText = t('enterAmount');
  let btnIcon = null;
  let btnClass = '';
  let btnDisabled = true;

  if (!isConnected) {
    btnText = t('connectWallet'); btnIcon = 'ph-wallet'; btnDisabled = false; btnClass = 'vp2-btn-connect';
  } else if (isFinalize && txStatus === 'loading') { btnText = t('btnExecuting'); btnIcon = 'ph-spinner spin-icon'; }
  else if (isFinalize && txStatus === 'success') { btnText = t('btnWithdrawalComplete'); btnIcon = 'ph-check'; btnClass = 'vp2-btn-success'; }
  else if (isFinalize && txStatus === 'error') { btnText = t('btnTryAgain'); btnIcon = 'ph-warning'; btnDisabled = false; }
  else if (isFinalize) { btnText = t('btnExecuteWithdrawal'); btnIcon = 'ph-arrow-right'; btnDisabled = !canFinalize; }
  else if (isOverBalance) { btnText = t('btnInsufficientBalance'); btnIcon = 'ph-warning-circle'; btnClass = 'vp2-btn-error'; }
  else if (txStatus === 'approving') { btnText = t('btnApprovingUsdc'); btnIcon = 'ph-spinner spin-icon'; }
  else if (txStatus === 'loading') { btnText = mode === 'deposit' ? t('btnBuyingShares') : t('btnSubmitting'); btnIcon = 'ph-spinner spin-icon'; }
  else if (txStatus === 'success') { btnText = mode === 'deposit' ? t('btnDepositComplete') : t('btnWithdrawalCompleteCheck'); btnClass = 'vp2-btn-success'; btnDisabled = false; }
  else if (txStatus === 'pending_timelock') { btnText = t('btnRequestSubmitted'); btnIcon = 'ph-clock'; btnClass = 'vp2-btn-success'; }
  else if (txStatus === 'error') { btnText = t('btnTryAgain'); btnIcon = 'ph-warning'; btnDisabled = false; }
  else if (canSubmit && mode === 'deposit') { btnText = t('btnSignDeposit'); btnIcon = 'ph-fingerprint'; btnDisabled = false; }
  else if (canSubmit && mode === 'withdraw' && isMorpho) { btnText = t('btnSignWithdraw'); btnIcon = 'ph-fingerprint'; btnDisabled = false; }
  else if (canSubmit && mode === 'withdraw') { btnText = t('btnRequestWithdrawal'); btnIcon = 'ph-clock-countdown'; btnDisabled = false; }

  function handleBtnClick() {
    if (!isConnected) { handleConnect(); return; }
    if (txStatus === 'success' || txStatus === 'pending_timelock') { resetForm(); loadVault(); return; }
    if (txStatus === 'error') { setTxStatus('idle'); setTxError(''); return; }
    executeTransaction();
  }

  const TF = ['1D', '1W', '1M', 'ALL'];

  /* ── Loading / not found ── */
  if (loading && !vaultData) {
    return (
      <AppLayout>
        <div className="vp2-state-center">
          <i className="ph-bold ph-spinner spin-icon" style={{ fontSize: 28, color: 'var(--t2)' }} />
          <span style={{ color: 'var(--t2)', fontSize: 14 }}>{t('loadingVault')}</span>
        </div>
      </AppLayout>
    );
  }
  if (!vaultData || !vault) {
    return (
      <AppLayout>
        <div className="vp2-state-center">
          <i className="ph-bold ph-warning" style={{ fontSize: 28, color: 'var(--danger)' }} />
          <span style={{ fontSize: 14, color: 'var(--t2)' }}>{t('vaultNotFound')}</span>
          <button className="vp2-back-btn" onClick={() => navigate('/strategies')}>← {t('backToStrategies')}</button>
        </div>
      </AppLayout>
    );
  }

  /* ── Estimated output for tx module ── */
  const outputAmount = mode === 'deposit'
    ? (amountNum > 0 ? shares.toFixed(6) : '0')
    : (amountNum > 0 ? netAmount.toFixed(2) : '0');
  const outputLabel = mode === 'deposit' ? vaultSymbol : 'USDC';

  const vaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: vault.name,
    url: `https://app.g12labs.com/vault/${vault.id}`,
    description: vault.description || `Actively managed DeFi vault on ${vault.network ?? 'Ethereum'}. Non-custodial and transparent.`,
    provider: { '@type': 'Organization', name: 'G12 Labs', url: 'https://g12labs.com' },
    feesAndCommissionsSpecification: vaultData?.config
      ? `Management: ${vaultData.config.management_fee_pct ?? 0}% / Performance: ${vaultData.config.performance_fee_pct ?? 0}%`
      : undefined,
  };
  const seoDesc = `${vault.name} — ${(perf.perf_30d_pct ?? 0).toFixed(2)}% 30d · $${(perf.total_aum ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} AUM. Actively managed, non-custodial DeFi vault on ${vault.network ?? 'Ethereum'}.`;

  return (
    <AppLayout>
      <Seo
        title={`${vault.name} — Vault | G12 Labs`}
        description={seoDesc.slice(0, 155)}
        path={`/vault/${vault.id}`}
        jsonLd={vaultJsonLd}
      />
      <div className="vp2-page">

        {/* ── Back ── */}
        <button className="vp2-back-btn" onClick={() => navigate('/strategies')}>
          <i className="ph-bold ph-arrow-left" /> {t('backToStrategies')}
        </button>

        {/* ── Vault hero header ── */}
        <div className="vp2-hero">
          <div className="vp2-hero-brand">
            <div className="vp2-hero-logos">
              <img src={vault.provider_logo_url ?? '/SVG-05.svg'} alt="" className="vp2-hero-logo" />
              <img src="/ethereum-eth-logo.png" alt="Ethereum" className="vp2-eth-badge" title="Ethereum" />
            </div>
            <div className="vp2-hero-main">
              <div className="vp2-hero-title-area">
                <h1 className="vp2-hero-title">{vault.name}</h1>
                <div className="vp2-hero-meta">
                  {inceptionStr && <span>{t('createdLabel')} {inceptionStr}</span>}
                  {inceptionStr && <span className="vp2-hero-meta-dot">·</span>}
                  <span>{t('lastUpdateLabel')} {lastUpdateStr}</span>
                </div>
              </div>
              <div className="vp2-hero-attrs">
                <div className="vp2-hero-attr">
                  <span className="vp2-hero-attr-label">{t('asset')}</span>
                  <div className="vp2-hero-attr-val">
                    <img src="/usd-coin-usdc-logo.png" alt="USDC" className="vp2-hero-attr-img" />
                    <span>USDC</span>
                  </div>
                </div>
                <div className="vp2-hero-attr-sep" />
                <div className="vp2-hero-attr">
                  <span className="vp2-hero-attr-label">{t('exposure')}</span>
                  <div className="vp2-hero-attr-val">
                    {getExposureTokens(vault.name).map((t, i) => (
                      <img key={t.symbol} src={t.logo} alt={t.symbol} className="vp2-hero-attr-img vp2-hero-attr-exp" title={t.symbol} style={{ marginLeft: i === 0 ? 0 : -6, zIndex: 10 - i }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="vp2-kpi-strip">
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('riskFactor')}</span>
              <span className="vp2-kpi-val vp2-kpi-dots">{riskDots(vault.risk_level)}</span>
            </div>
            <div className="vp2-kpi-sep" />
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('sharePrice')}</span>
              <span className="vp2-kpi-val">{fmtUsd(sharePrice)}</span>
            </div>
            <div className="vp2-kpi-sep" />
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('return7d')}</span>
              <span className={`vp2-kpi-val ${(perf?.perf_7d_pct ?? 0) >= 0 ? 'vp2-pos' : 'vp2-neg'}`}>
                {fmtPct(perf?.perf_7d_pct)}
              </span>
            </div>
            <div className="vp2-kpi-sep" />
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('return30d')}</span>
              <span className={`vp2-kpi-val ${(perf?.perf_30d_pct ?? 0) >= 0 ? 'vp2-pos' : 'vp2-neg'}`}>
                {fmtPct(perf?.perf_30d_pct)}
              </span>
            </div>
            <div className="vp2-kpi-sep" />
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('aum')}</span>
              <span className="vp2-kpi-val">{fmtUsd(perf?.total_aum)}</span>
            </div>
            <div className="vp2-kpi-sep" />
            <div className="vp2-kpi">
              <span className="vp2-kpi-label">{t('depositors')}</span>
              <span className="vp2-kpi-val">{depositorCount}</span>
            </div>
          </div>
        </div>

        {/* ── 3-area grid: chart / tx / info ── */}
        <div className="vp2-grid">

          {/* ══ CHART ══ */}
          <div className="vp2-chart-col">
            <div className="vp2-chart-card">
              <div className="vp2-chart-top">
                <div className="vp2-chart-mode-tabs">
                  <button className={`vp2-mode-btn ${chartMode === 'price' ? 'active' : ''}`} onClick={() => setChartMode('price')}>{t('sharePrice')}</button>
                  <button className={`vp2-mode-btn ${chartMode === 'aum' ? 'active' : ''}`} onClick={() => setChartMode('aum')}>{t('aum')}</button>
                  {chartMode === 'aum' && (
                    <button
                      className={`vp2-mode-btn vp2-mode-btn--markers ${showMarkers ? 'active' : ''}`}
                      onClick={() => setShowMarkers(v => !v)}
                      title="Toggle deposit/withdrawal markers"
                    >
                      ◈ D/W
                    </button>
                  )}
                </div>
                <div className="vp2-tf-tabs">
                  {TF.map(tf => (
                    <button
                      key={tf}
                      className={`vp2-tf-btn ${timeframe === tf ? 'active' : ''}`}
                      onClick={() => setTimeframe(tf)}
                    >{tf}</button>
                  ))}
                </div>
              </div>
              <div className="vp2-chart-body">
                <VaultChart data={activeChartData} positive={chartPositive} mode={chartMode} timeframe={timeframe} theme={theme} markers={chartMarkers} />
              </div>
            </div>
          </div>

          {/* ══ INFO — Fees + Position ══ */}
          <div className="vp2-left">
            <div className="vp2-info-card">
              <h3 className="vp2-info-title">{t('feeStructure')}</h3>
              <div className="vp2-fee-table">
                {[
                  [t('entranceFee'),    fmtFee(config?.entrance_fee_pct)],
                  [t('exitFee'),        fmtFee(config?.exit_fee_pct)],
                  [t('managementFee'),  fmtFee(config?.management_fee_pct)],
                  [t('performanceFee'), fmtFee(config?.performance_fee_pct)],
                  [t('highWaterMark'), config?.high_watermark ? t('yes') : t('no')],
                  [t('timelock'),       isMorpho ? t('noneInstant') : t('timelockDays', { n: config?.timelock_days ?? 3 })],
                ].map(([label, val]) => (
                  <div key={label} className="vp2-fee-row">
                    <span>{label}</span><span>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ══ RIGHT — Position + Transaction module ══ */}
          <div className="vp2-tx-sticky">

            {/* Your Position — separate card above deposit */}
            {userPos && (
              <div className="vp2-pos-banner">
                <div className="vp2-pos-banner-left">
                  <i className="ph-fill ph-wallet" />
                  <span className="vp2-pos-banner-label">{t('yourPosition')}</span>
                </div>
                <div className="vp2-pos-banner-right">
                  <span className="vp2-pos-banner-val">{fmtUsd(userPos.value_usdc)}</span>
                  <span className="vp2-pos-banner-shares">{Number(userPos.current_shares ?? 0).toFixed(4)} {vaultSymbol}</span>
                </div>
              </div>
            )}

            <div className="vp2-tx-card-wrap">
            <div className="vp2-tx-card">

              {/* Buy / Sell segmented control */}
              <div className="vp2-seg-ctrl">
                <button
                  className={`vp2-seg-btn ${mode === 'deposit' ? 'active' : ''}`}
                  onClick={() => { setMode('deposit'); setAmount(''); setUseMaxShares(false); setTxStatus('idle'); setTxError(''); }}
                >{t('buy')}</button>
                <button
                  className={`vp2-seg-btn ${mode === 'withdraw' ? 'active' : ''}`}
                  onClick={() => { setMode('withdraw'); setAmount(''); setUseMaxShares(false); setTxStatus('idle'); setTxError(''); }}
                >{t('sell')}</button>
              </div>

              {/* Pending redemptions */}
              {mode === 'withdraw' && !isFinalize && !isMorpho && pendingRedemptions.map(r => (
                <PendingBanner
                  key={r.id}
                  redemption={r}
                  vaultSymbol={vaultSymbol}
                  sharePrice={sharePrice}
                  canceling={cancelingId === r.id}
                  onCancel={() => handleCancelPending(r.id)}
                  onExecute={() => handleFinalizePending(r)}
                />
              ))}

              {/* ── YOU PAY ── */}
              <div className="vp2-swap-box">
                <div className="vp2-swap-label">{t('youPay')}</div>
                <div className="vp2-swap-row">
                  <input
                    type="number"
                    className="vp2-swap-input"
                    placeholder="0"
                    value={isFinalize ? (Number(urlShares) * sharePrice).toFixed(2) : amount}
                    onChange={e => { setAmount(e.target.value); setUseMaxShares(false); }}
                    readOnly={isFinalize}
                    min="0"
                    step="any"
                  />
                  <div className="vp2-swap-asset">
                    <img
                      src={mode === 'deposit' ? '/usd-coin-usdc-logo.png' : (vault?.provider_logo_url ?? '/SVG-05.svg')}
                      alt={mode === 'deposit' ? 'USDC' : vaultSymbol}
                      className="vp2-swap-asset-logo"
                    />
                    <span>{mode === 'deposit' ? 'USDC' : vaultSymbol}</span>
                  </div>
                </div>
                <div className="vp2-swap-sub">
                  {isConnected && (
                    <>
                      <span style={{ color: 'var(--t3)' }}>
                        {t('balance')}:{' '}
                        {mode === 'deposit'
                          ? `${walletUsdcBalance.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDC`
                          : `${(userPos?.current_shares ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 })} ${vaultSymbol}`
                        }
                      </span>
                      {!isFinalize && (
                        <button className="vp2-max-btn" onClick={() => {
                          if (mode === 'deposit') setAmount(walletUsdcBalance.toString());
                          else { setAmount(maxBalance.toString()); setUseMaxShares(true); }
                        }}>Max</button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* ── Arrow ── */}
              <div className="vp2-swap-arrow">
                <i className="ph-bold ph-arrow-down" />
              </div>

              {/* ── YOU RECEIVE ── */}
              <div className="vp2-swap-box vp2-swap-box-out">
                <div className="vp2-swap-label">{t('youReceive')}</div>
                <div className="vp2-swap-row">
                  <div className="vp2-swap-output">{outputAmount}</div>
                  <div className="vp2-swap-asset">
                    <img
                      src={mode === 'deposit' ? (vault?.provider_logo_url ?? '/SVG-05.svg') : '/usd-coin-usdc-logo.png'}
                      alt={outputLabel}
                      className="vp2-swap-asset-logo"
                    />
                    <span>{outputLabel}</span>
                  </div>
                </div>
                <div className="vp2-swap-sub">
                  {amountNum > 0 && (
                    <span style={{ color: 'var(--t3)' }}>
                      {mode === 'deposit'
                        ? `≈ ${fmtUsd(netAmount)}`
                        : `${shares.toLocaleString('en-US', { maximumFractionDigits: 4 })} ${vaultSymbol}`
                      }
                    </span>
                  )}
                </div>
              </div>

              {/* ── Transaction details accordion ── */}
              <button
                className="vp2-details-toggle"
                onClick={() => setDetailsOpen(o => !o)}
              >
                <span><i className="ph ph-info" /> {t('txDetails')}</span>
                <i className={`ph-bold ph-caret-down vp2-caret ${detailsOpen ? 'open' : ''}`} />
              </button>

              {detailsOpen && (
                <div className="vp2-details-panel">
                  {feeAmount > 0 && (
                    <div className="vp2-detail-row">
                      <span>{mode === 'deposit' ? t('entranceFeeLabel') : t('exitFeeLabel')}</span>
                      <span style={{ color: 'var(--danger)' }}>-{fmtUsd(feeAmount)}</span>
                    </div>
                  )}
                  <div className="vp2-detail-row">
                    <span>{t('netAmount')}</span>
                    <span>{fmtUsd(netAmount)}</span>
                  </div>
                  {gasUSD != null && (
                    <div className="vp2-detail-row">
                      <span>{t('estNetworkFee')}</span>
                      <span>~${gasUSD.toFixed(2)} <span style={{ color: 'var(--t3)', fontSize: 11 }}>({gasTimer}s)</span></span>
                    </div>
                  )}
                  {!isMorpho && mode === 'withdraw' && !isFinalize && (
                    <div className="vp2-detail-row vp2-detail-warn">
                      <i className="ph-bold ph-clock-countdown" />
                      <span>{t('timelockWarning')}</span>
                    </div>
                  )}
                  {isOverBalance && (
                    <div className="vp2-detail-row vp2-detail-warn">
                      <i className="ph-bold ph-warning" />
                      <span>{t('exceedsBalance')}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {txError && (
                <div className="vp2-tx-error">
                  <i className="ph-bold ph-warning-circle" /> {txError}
                </div>
              )}

              {/* ── CTA ── */}
              <button
                className={`vp2-cta ${btnClass}`}
                disabled={btnDisabled && isConnected}
                onClick={handleBtnClick}
              >
                {btnIcon && <i className={`ph-bold ${btnIcon}`} />}
                {btnText}
              </button>

            </div>
            </div>{/* /vp2-tx-card-wrap */}
          </div>
        </div>

        {/* ══ BOTTOM TABS ══ */}
        <div className="vp2-tabs-section">
          <div className="vp2-tabs-nav">
            {[
              { key: 'depositors',  label: t('depositorsTab') },
              { key: 'activity',    label: t('activityTab') },
              { key: 'deposits',    label: t('depositsTab') },
              { key: 'withdrawals', label: t('withdrawalsTab') },
              { key: 'stats',       label: t('statsTab') },
            ].map(tb => (
              <button
                key={tb.key}
                className={`vp2-tab-btn ${activeTab === tb.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tb.key)}
              >{tb.label}</button>
            ))}
          </div>

          {/* Depositors tab */}
          {activeTab === 'depositors' && (() => {
            const visibleDepositors = depositors.filter(d => d.wallet_address?.toLowerCase() !== feeRecipientAddress);
            const totalShares = visibleDepositors.reduce((s, d) => s + Number(d.current_shares), 0);
            return (
              <div className="vp2-tab-body">
                {visibleDepositors.length === 0 ? (
                  <div className="vp2-tab-empty">
                    <i className="ph-bold ph-users" />
                    <span>{t('noDepositors')}</span>
                  </div>
                ) : (
                  <>
                    <div className="vp2-tab-count">{visibleDepositors.length} {t('uniqueDepositors')}</div>
                    <div className="vp2-activity-table vp2-depositors-table">
                      <div className="vp2-activity-header">
                        <span>#</span>
                        <span>{t('walletTh')}</span>
                        <span>{t('sharesTh')}</span>
                        <span>{t('valueTh')}</span>
                        <span>%</span>
                      </div>
                      {visibleDepositors.slice(tabPage * ROWS_PER_PAGE, (tabPage + 1) * ROWS_PER_PAGE).map((d, i) => {
                        const shares = Number(d.current_shares);
                        const val = shares * sharePrice;
                        const pct = totalShares > 0 ? (shares / totalShares * 100) : 0;
                        const addr = d.wallet_address ? `${d.wallet_address.slice(0,6)}…${d.wallet_address.slice(-4)}` : '—';
                        const wa = d.wallet_address?.toLowerCase();
                        const isManager = managerAddress && wa === managerAddress;
                        const isFeeRecipient = feeRecipientAddress && wa === feeRecipientAddress;
                        return (
                          <div key={i} className="vp2-activity-row">
                            <span className="mono" style={{ color: 'var(--t2)', fontSize: 11 }}>{tabPage * ROWS_PER_PAGE + i + 1}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span className="mono" style={{ fontSize: 11 }}>{addr}</span>
                              <a href={`https://etherscan.io/address/${d.wallet_address}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-link" onClick={e => e.stopPropagation()}>
                                <i className="ph-bold ph-arrow-square-out" />
                              </a>
                              {isManager && <span className="vp2-addr-badge vp2-addr-badge--manager">Manager</span>}
                              {isFeeRecipient && <span className="vp2-addr-badge vp2-addr-badge--fee">Fees</span>}
                            </span>
                            <span className="mono">{shares.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                            <span className="mono vp2-tx-dep">{fmtUsd(val)}</span>
                            <span className="mono" style={{ color: 'var(--t2)', fontSize: 12 }}>{pct.toFixed(1)}%</span>
                          </div>
                        );
                      })}
                    </div>
                    <Pagination total={visibleDepositors.length} page={tabPage} onChange={setTabPage} />
                  </>
                )}
              </div>
            );
          })()}

          {/* Activity tab — strategy on-chain operations */}
          {activeTab === 'activity' && (
            <div className="vp2-tab-body">
              {isMorpho ? (
                <div className="vp2-tab-empty">
                  <i className="ph-bold ph-clock" />
                  <span>{t('activitySoon')}</span>
                </div>
              ) : vaultOnchainActivity.length === 0 ? (
                <div className="vp2-tab-empty">
                  <i className="ph-bold ph-activity" />
                  <span>{t('noActivity')}</span>
                </div>
              ) : (
                <>
                  <div className="vp2-tab-count">{vaultOnchainActivity.length} {t('totalRecords')}</div>
                  <div className="vp2-activity-table vp2-activity-onchain">
                    <div className="vp2-activity-header">
                      <span>{t('typeTh')}</span>
                      <span>Details</span>
                      <span>{t('dateTh')}</span>
                      <span>{t('txTh')}</span>
                    </div>
                    {vaultOnchainActivity.slice(tabPage * ROWS_PER_PAGE, (tabPage + 1) * ROWS_PER_PAGE).map((tx, i) => {
                      const date = tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const method = tx.method_name ?? tx.activity_kind ?? 'call';
                      const parentTx = tx.activity_details?.parentTxHash ?? tx.tx_hash?.split('/')[0];
                      return (
                        <div key={i} className="vp2-activity-row">
                          <span>
                            <span className="vp2-method-badge">
                              <ProtocolLogo kind={tx.activity_kind} size={13} />
                              {method}
                            </span>
                          </span>
                          <span><ActivityDetails kind={tx.activity_kind} details={tx.activity_details} /></span>
                          <span style={{ color: 'var(--t2)', fontSize: 12 }}>{date}</span>
                          <span>
                            {parentTx ? (
                              <a href={`https://etherscan.io/tx/${parentTx}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-link" onClick={e => e.stopPropagation()}>
                                <i className="ph-bold ph-arrow-square-out" />
                              </a>
                            ) : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination total={vaultOnchainActivity.length} page={tabPage} onChange={setTabPage} />
                </>
              )}
            </div>
          )}

          {/* Deposits tab */}
          {activeTab === 'deposits' && (
            <div className="vp2-tab-body">
              {vaultDeposits.length === 0 ? (
                <div className="vp2-tab-empty">
                  <i className="ph-bold ph-arrow-down-left" />
                  <span>{t('noDeposits')}</span>
                </div>
              ) : (
                <>
                  <div className="vp2-tab-count">{vaultDeposits.length} {t('depositsTab')}</div>
                  <div className="vp2-activity-table">
                    <div className="vp2-activity-header">
                      <span>{t('walletTh')}</span>
                      <span>{t('amount')}</span>
                      <span>{t('sharesTh')}</span>
                      <span>{t('sharePrice')}</span>
                      <span>{t('dateTh')}</span>
                      <span>{t('txTh')}</span>
                    </div>
                    {vaultDeposits.slice(tabPage * ROWS_PER_PAGE, (tabPage + 1) * ROWS_PER_PAGE).map((tx, i) => {
                      const date = tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const addr = tx.wallet_address ? `${tx.wallet_address.slice(0,6)}…${tx.wallet_address.slice(-4)}` : '—';
                      const sharesNum = Number(tx.shares_amount);
                      const spNum = Number(tx.share_price_at_tx);
                      const spDisplay = tx.share_price_at_tx != null && sharesNum > 0.001 && spNum < 100
                        ? `$${spNum.toFixed(4)}` : '—';
                      return (
                        <div key={i} className="vp2-activity-row">
                          <span className="mono" style={{ fontSize: 11, color: 'var(--t2)' }}>{addr}</span>
                          <span className="mono vp2-tx-dep">{fmtUsd(Number(tx.usdc_amount))}</span>
                          <span className="mono">{sharesNum > 0.001 ? sharesNum.toFixed(4) : '—'}</span>
                          <span className="mono">{spDisplay}</span>
                          <span style={{ color: 'var(--t2)', fontSize: 12 }}>{date}</span>
                          <span>
                            {tx.tx_hash ? (
                              <a href={`https://etherscan.io/tx/${tx.tx_hash}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-link" onClick={e => e.stopPropagation()}>
                                <i className="ph-bold ph-arrow-square-out" />
                              </a>
                            ) : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination total={vaultDeposits.length} page={tabPage} onChange={setTabPage} />
                </>
              )}
            </div>
          )}

          {/* Withdrawals tab */}
          {activeTab === 'withdrawals' && (
            <div className="vp2-tab-body">
              {vaultWithdrawals.length === 0 ? (
                <div className="vp2-tab-empty">
                  <i className="ph-bold ph-arrow-up-right" />
                  <span>{t('noWithdrawals')}</span>
                </div>
              ) : (
                <>
                  <div className="vp2-tab-count">{vaultWithdrawals.length} {t('withdrawalsTab')}</div>
                  <div className="vp2-activity-table">
                    <div className="vp2-activity-header">
                      <span>{t('walletTh')}</span>
                      <span>{t('amount')}</span>
                      <span>{t('sharesTh')}</span>
                      <span>{t('sharePrice')}</span>
                      <span>{t('dateTh')}</span>
                      <span>{t('txTh')}</span>
                    </div>
                    {vaultWithdrawals.slice(tabPage * ROWS_PER_PAGE, (tabPage + 1) * ROWS_PER_PAGE).map((tx, i) => {
                      const date = tx.timestamp ? new Date(tx.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
                      const addr = tx.wallet_address ? `${tx.wallet_address.slice(0,6)}…${tx.wallet_address.slice(-4)}` : '—';
                      const sharesNum = Number(tx.shares_amount);
                      const spNum = Number(tx.share_price_at_tx);
                      const spDisplay = tx.share_price_at_tx != null && sharesNum > 0.001 && spNum < 100
                        ? `$${spNum.toFixed(4)}` : '—';
                      return (
                        <div key={i} className="vp2-activity-row">
                          <span className="mono" style={{ fontSize: 11, color: 'var(--t2)' }}>{addr}</span>
                          <span className="mono vp2-tx-wdw">{fmtUsd(Number(tx.usdc_amount))}</span>
                          <span className="mono">{sharesNum > 0.001 ? sharesNum.toFixed(4) : '—'}</span>
                          <span className="mono">{spDisplay}</span>
                          <span style={{ color: 'var(--t2)', fontSize: 12 }}>{date}</span>
                          <span>
                            {tx.tx_hash ? (
                              <a href={`https://etherscan.io/tx/${tx.tx_hash}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-link" onClick={e => e.stopPropagation()}>
                                <i className="ph-bold ph-arrow-square-out" />
                              </a>
                            ) : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination total={vaultWithdrawals.length} page={tabPage} onChange={setTabPage} />
                </>
              )}
            </div>
          )}

          {/* Stats tab */}
          {activeTab === 'stats' && (
            <div className="vp2-tab-body">
              <div className="vp2-stat-grid">
                {[
                  { label: t('sharePrice'),   val: fmtUsd(sharePrice), mono: true },
                  { label: t('totalAum'),     val: fmtUsd(perf?.total_aum), mono: true },
                  { label: t('totalShares'),  val: perf?.total_shares != null ? Number(perf.total_shares).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—', mono: true },
                  { label: t('depositors'),   val: depositorCount, mono: true },
                  { label: t('return1d'),     val: fmtPct(perf?.perf_1d_pct),  mono: true, color: (perf?.perf_1d_pct ?? 0) >= 0 ? 'pos' : 'neg' },
                  { label: t('return7dStat'), val: fmtPct(perf?.perf_7d_pct),  mono: true, color: (perf?.perf_7d_pct ?? 0) >= 0 ? 'pos' : 'neg' },
                  { label: t('return30dStat'),val: fmtPct(perf?.perf_30d_pct), mono: true, color: (perf?.perf_30d_pct ?? 0) >= 0 ? 'pos' : 'neg' },
                  { label: t('sharpeRatio'),  val: sharpeRatio ?? '—', mono: true },
                  { label: t('network'),      val: vault.network ?? 'Ethereum' },
                  { label: t('inception'),    val: vault.inception_date ? new Date(vault.inception_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—', mono: true },
                ].map(s => (
                  <div key={s.label} className="vp2-stat-cell">
                    <span className="vp2-stat-cell-label">{s.label}</span>
                    <span className={`vp2-stat-cell-val ${s.mono ? 'mono' : ''} ${s.color === 'pos' ? 'vp2-pos' : ''} ${s.color === 'neg' ? 'vp2-neg' : ''}`}>
                      {s.val}
                    </span>
                  </div>
                ))}
                {vault.address && (
                  <div className="vp2-stat-cell vp2-stat-cell-addr">
                    <span className="vp2-stat-cell-label">{t('vaultAddress')}</span>
                    <div className="vp2-stat-addr">
                      <span className="mono">{vault.address.slice(0,8)}…{vault.address.slice(-6)}</span>
                      <a href={`https://etherscan.io/address/${vault.address}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-badge">
                        <i className="ph-bold ph-arrow-square-out" />
                      </a>
                    </div>
                  </div>
                )}
                {vault.manager_address && (
                  <div className="vp2-stat-cell vp2-stat-cell-addr">
                    <span className="vp2-stat-cell-label">{t('manager')}</span>
                    <div className="vp2-stat-addr">
                      <span className="vp2-addr-badge vp2-addr-badge--manager">Manager</span>
                      <span className="mono">{vault.manager_address.slice(0,8)}…{vault.manager_address.slice(-6)}</span>
                      <a href={`https://etherscan.io/address/${vault.manager_address}`} target="_blank" rel="noopener noreferrer" className="vp2-etherscan-badge">
                        <i className="ph-bold ph-arrow-square-out" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </AppLayout>
  );
}
